import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** Decorative film with a keyboard-accessible pause control and no scrubber. */
class EvermoreVideo extends Component {
  #controller;
  #observer;
  #userPaused = false;
  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }
  #bind() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const video = this.querySelector('video');
    const button = this.querySelector('[data-video-toggle]');
    if (!video || !button) return;
    const update = () => {
      button.setAttribute('aria-label', video.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      button.dataset.paused = String(video.paused);
    };
    button.addEventListener('click', () => {
      this.#userPaused = !video.paused;
      if (video.paused) { this.#userPaused = false; video.play().catch(() => {}); }
      else { this.#userPaused = true; video.pause(); }
    }, { signal: this.#controller.signal });
    video.addEventListener('play', update, { signal: this.#controller.signal });
    video.addEventListener('pause', update, { signal: this.#controller.signal });
    this.#observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && !this.#userPaused && !matchMedia('(prefers-reduced-motion: reduce)').matches) video.play().catch(() => {});
      else video.pause();
    }, { threshold: 0.2 });
    this.#observer.observe(this);
    update();
  }
}
if (!customElements.get('evermore-video')) customElements.define('evermore-video', EvermoreVideo);

/**
 * Campaign product UI; Shopify owns prices, availability and cart line identity.
 * @extends {Component<{gallery: HTMLElement, counter: HTMLElement, variant: HTMLInputElement | HTMLSelectElement | HTMLFieldSetElement, quantity: HTMLInputElement, variants: HTMLScriptElement, personalisation: HTMLDetailsElement, stock: HTMLElement, status: HTMLElement}>}
 */
/**
 * Characters the engraving supplier can cut: Latin letters (with accents), digits, spaces and & . - '
 * @param {string} value
 * @returns {string} The distinct unsupported characters, or an empty string.
 */
function unsupportedEngraving(value) {
  const blocked = new Set(Array.from(value).filter(char => !/[\p{Script=Latin}\p{M}0-9 &.'-]/u.test(char)));
  return [...blocked].join(' ');
}

class EvermoreProduct extends Component {
  #controller;
  #observer;
  #busy = false;
  #index = 0;
  #variants = [];
  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }
  #bind() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#variants = JSON.parse(this.refs.variants.textContent || '[]');
    this.refs.variant.addEventListener('change', () => this.#variantChanged(), options);
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const step = event.target.closest('[data-step]');
      const slide = event.target.closest('[data-slide]');
      if (step) this.#go(this.#index + Number(step.dataset.step));
      if (slide) this.#go(Number(slide.dataset.slide));
    }, options);
    this.refs.gallery.addEventListener('scroll', () => this.#galleryState(), { ...options, passive: true });
    this.refs.gallery.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const rtl = getComputedStyle(this).direction === 'rtl';
        this.#go(this.#index + (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      }
    }, options);
    for (const input of this.querySelectorAll('[data-name]')) {
      input.addEventListener('input', () => {
        this.#input(input);
        const preview = this.querySelector('.em-preview');
        if (preview instanceof HTMLDetailsElement && input.value.trim() && !preview.dataset.autoOpened) {
          preview.open = true;
          preview.dataset.autoOpened = 'true';
        }
      }, options);
      input.addEventListener('invalid', () => { if (this.refs.personalisation) this.refs.personalisation.open = true; }, options);
      this.#input(input);
    }
    this.querySelector('form')?.addEventListener('submit', event => this.#submit(event), options);
    this.#variantChanged(false);
    this.#galleryState();
  }
  #input(input) {
    // Smart apostrophes from phone keyboards are engraved as a plain apostrophe.
    if (input.value.includes('’')) input.value = input.value.replace(/’/g, "'");
    const count = Array.from(input.value).length;
    const max = Number(input.dataset.max);
    const blocked = unsupportedEngraving(input.value);
    const charsMessage = blocked ? (this.dataset.chars || '').replace('__CHARS__', blocked) : '';
    input.setCustomValidity(input.value && !input.value.trim() ? this.dataset.required : charsMessage || (count > max ? this.dataset.limit.replace('{limit}', String(max)) : ''));
    const error = this.querySelector('[data-error-for="' + input.dataset.name + '"]');
    if (error) { error.textContent = charsMessage; error.hidden = !charsMessage; }
    input.toggleAttribute('aria-invalid', Boolean(charsMessage));
    const counter = this.querySelector('[data-count="' + input.dataset.name + '"]');
    if (counter) counter.textContent = count + ' / ' + max;
    const preview = this.querySelector('[data-preview="' + input.dataset.name + '"]');
    if (preview) preview.textContent = input.value;
  }
  #validate() {
    for (const input of this.querySelectorAll('[data-name]')) this.#input(input);
    const form = this.querySelector('form');
    if (!form?.checkValidity() && this.refs.personalisation) this.refs.personalisation.open = true;
    return Boolean(form?.reportValidity());
  }
  #variantChanged(updateUrl = true) {
    const selectedId = this.refs.variant instanceof HTMLFieldSetElement
      ? this.refs.variant.querySelector('input[name="id"]:checked')?.value
      : this.refs.variant.value;
    const variant = this.#variants.find(v => String(v.id) === selectedId);
    if (!variant) return;
    if (this.dataset.personalisationMode === 'names') {
      const count = variant.names;
      for (const field of this.querySelectorAll('[data-name-field]')) {
        const active = Number(field.dataset.nameField) <= count;
        field.hidden = !active;
        const input = field.querySelector('input');
        if (input) { input.disabled = !active; input.required = active; }
      }
    }
    for (const price of document.querySelectorAll('.evermore-page [data-em-price]')) price.textContent = variant.price;
    for (const price of this.querySelectorAll('.em-price')) price.classList.toggle('em-price--sale', Boolean(variant.compare));
    for (const discount of this.querySelectorAll('[data-em-discount]')) {
      discount.hidden = !variant.discount || variant.discount === '-0%';
      discount.textContent = variant.discount || '';
    }
    for (const compare of document.querySelectorAll('.evermore-page [data-em-compare]')) {
      const label = compare.querySelector('.visually-hidden');
      compare.hidden = !variant.compare;
      for (const node of [...compare.childNodes]) if (node !== label) node.remove();
      if (variant.compare) compare.append(variant.compare);
    }
    const nativePrice = this.querySelector('.add-to-cart-button__price');
    if (nativePrice) { nativePrice.textContent = variant.price; nativePrice.hidden = !variant.available; }
    const nativeLabel = this.querySelector('.add-to-cart-button__label');
    if (nativeLabel) nativeLabel.textContent = variant.available ? this.dataset.add : this.dataset.soldOut;
    else {
      const nativeButton = this.querySelector('add-to-cart-component button');
      const labelNode = [...(nativeButton?.childNodes || [])].find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (labelNode) labelNode.textContent = variant.available ? this.dataset.add : this.dataset.soldOut;
    }
    for (const label of this.querySelectorAll('[data-em-add]')) label.textContent = variant.available ? this.dataset.add : this.dataset.soldOut;
    for (const button of this.querySelectorAll('[type="submit"], [data-sticky-add]')) button.disabled = this.#busy || !variant.available;
    this.refs.quantity.value = String(variant.quantity);
    this.refs.stock.hidden = !(variant.available && variant.low);
    if (updateUrl) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  }
  #go(index) {
    const slides = this.refs.gallery.children;
    this.#index = Math.max(0, Math.min(slides.length - 1, index));
    slides[this.#index]?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest', inline: 'start' });
  }
  #galleryState() {
    const gallery = this.refs.gallery;
    this.#index = Math.round(Math.abs(gallery.scrollLeft) / Math.max(1, gallery.clientWidth));
    this.refs.counter.textContent = (this.#index + 1) + '/' + gallery.children.length;
    for (const button of this.querySelectorAll('[data-slide]')) button.setAttribute('aria-pressed', String(Number(button.dataset.slide) === this.#index));
    this.querySelector('[data-step="-1"]').disabled = this.#index === 0;
    this.querySelector('[data-step="1"]').disabled = this.#index >= gallery.children.length - 1;
    for (const [i, slide] of [...gallery.children].entries()) if (i !== this.#index) slide.querySelector('video')?.pause();
  }
  /** @param {SubmitEvent} event */
  async #submit(event) {
    event.preventDefault();
    if (this.#busy || !this.#validate()) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    this.#busy = true;
    this.#variantChanged(false);
    this.refs.status.classList.remove('visually-hidden');
    this.refs.status.textContent = this.dataset.pending;
    const pending = CartLinesUpdateEvent.createPromise();
    // A listener may not exist on a page-only cart, so always handle rejection.
    pending.promise.catch(() => {});
    const body = new FormData(form);
    let added = false;
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId);
    body.set('sections', [...new Set(sectionIds)].slice(0, 5).join(','));
    body.set('sections_url', location.pathname);
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add', context:'product', lines:[{merchandiseId:String(body.get('id')), quantity:Number(body.get('quantity'))}], promise:pending.promise}));
    try {
      const response = await fetch(this.dataset.addUrl + '.js', {method:'POST', body, headers:{Accept:'application/json'}, signal:this.#controller.signal});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
      added = true;
      const cartResponse = await fetch(this.dataset.cartUrl + '.js', {headers:{Accept:'application/json'}, signal:this.#controller.signal});
      if (!cartResponse.ok) throw new Error(this.dataset.error);
      const cart = await cartResponse.json();
      pending.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail:{source:'evermore-product', sourceId:this.id, items:cart.items, sections:result.sections, didError:false}});
      this.refs.status.classList.add('visually-hidden');
      this.refs.status.textContent = this.dataset.added;
      for (const badge of document.querySelectorAll('[data-em-cart-count]')) badge.textContent = String(cart.item_count);
      const drawer = document.getElementById('cart-drawer');
      if (drawer?.open) drawer.open();
      else location.assign(this.dataset.cartUrl);
    } catch (error) {
      pending.reject(error);
      if (added) {
        this.refs.status.classList.add('visually-hidden');
        this.refs.status.textContent = this.dataset.added;
        location.assign(this.dataset.cartUrl);
      } else if (error.name !== 'AbortError') {
        this.refs.status.textContent = error.message || this.dataset.error;
      }
    } finally {
      this.#busy = false;
      if (this.isConnected) this.#variantChanged(false);
    }
  }
}
if (!customElements.get('evermore-product')) customElements.define('evermore-product', EvermoreProduct);

// Header badges follow the same event promise as the native cart components.
document.addEventListener(StandardEvents.cartLinesUpdate, async event => {
  try {
    const update = await event.promise;
    if (!update?.detail?.items) return;
    const count = update.detail.items.reduce((sum, item) => sum + item.quantity, 0);
    for (const badge of document.querySelectorAll('[data-em-cart-count]')) badge.textContent = String(count);
  } catch { /* The initiating component owns failure feedback. */ }
});
